import { useId, useState, type ReactNode } from "react";
import { ChevronDown, CircleHelp } from "lucide-react";

import { cn } from "@/common/utils/className";
import { Button } from "@/domain/ui/Button";
import { EXPANDABLE_HELP_VARIANT, type ExpandableHelpVariant } from "./expandableHelpVariants";

interface ExpandableHelpProps {
  label: string;
  expandedLabel?: string;
  variant?: ExpandableHelpVariant;
  children: ReactNode;
}

export function ExpandableHelp({
  label,
  expandedLabel,
  variant = EXPANDABLE_HELP_VARIANT.MUTED,
  children,
}: ExpandableHelpProps) {
  const [isOpen, setIsOpen] = useState(false);
  const contentId = useId();
  const isUnderline = variant === EXPANDABLE_HELP_VARIANT.UNDERLINE;

  return (
    <div>
      <Button
        type="button"
        variant="link"
        size="sm"
        className={cn(
          "h-auto max-w-full whitespace-normal px-0 py-0 text-left",
          isUnderline
            ? "text-foreground font-normal underline decoration-foreground/35 underline-offset-4"
            : "text-muted-foreground text-xs",
        )}
        leadingIcon={isUnderline ? <CircleHelp aria-hidden="true" className="size-4" /> : undefined}
        trailingIcon={<ChevronDown className={cn("size-3 transition-transform", isOpen && "rotate-180")} />}
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (expandedLabel ?? label) : label}
      </Button>
      <div id={contentId} hidden={!isOpen}>
        {children}
      </div>
    </div>
  );
}
